/**
 * symbolsApi — client for /api/symbols/*, the warehouse-backed instrument
 * resolver and daily-price source. Matching is soft: ISIN resolves exactly,
 * ticker to its listing, fund name via normalised prefix; on failure callers
 * fall back to the manual resolution flow.
 */
import type { WarehouseLink } from './portfolioApi';
import { isPositiveFinite } from '@/core/utils';
import { getJson, getJsonOrThrow, getJsonResult } from './http';
import type { DividendBasis, DividendFeed, DividendPoint } from './calc/types';

export interface SymbolMatch {
  /** The instrument's EODHD source_code (SPY.US, ISF.LSE, IE00….EUFUND) — the
   *  ONE form a ticker is stored in (WORK.md item 43). Absent only on matches
   *  built from saved settings rather than returned by the warehouse. */
  code?: string | null;
  name: string;
  type: string;
  isin: string | null;
  ticker: string | null;
  exchange: string | null;
  currency: string | null;
}

/** First present identifier as an encoded query-string pair; '' when none.
 * Callers pass pairs in THEIR precedence order (lookup/prices prefer ISIN,
 * splits/dividends prefer ticker). */
function idQuery(...pairs: Array<[key: string, value: string | undefined]>): string {
  for (const [k, v] of pairs) if (v) return `${k}=${encodeURIComponent(v)}`;
  return '';
}

export async function lookupSymbol(
  query: { isin?: string; ticker?: string; name?: string },
  opts: { signal?: AbortSignal } = {},
): Promise<SymbolMatch[]> {
  const qs = idQuery(['isin', query.isin], ['ticker', query.ticker])
    || `name=${encodeURIComponent(query.name ?? '')}`;
  const j = await getJsonOrThrow<{ matches?: SymbolMatch[] }>(
    `/api/symbols/lookup?${qs}`, 'symbol lookup',
    { credentials: 'include', signal: opts.signal },
  );
  return dedupeMatches(Array.isArray(j.matches) ? j.matches : []);
}

/** Drop exact-duplicate matches — an instrument joined to several identical
 * identifier/listing rows reads as the same entry repeated to a user. Applied
 * client-side so week-old cached lookup results are cleaned too. */
export function dedupeMatches(matches: SymbolMatch[]): SymbolMatch[] {
  const seen = new Set<string>();
  return matches.filter((m) => {
    const k = JSON.stringify([m.code ?? null, m.name, m.isin, m.ticker, m.exchange, m.currency]);
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}

export const ISIN_RE = /^[A-Z]{2}[A-Z0-9]{9}[0-9]$/;

/** Is this identifier ISIN-shaped? */
export function isIsin(v: string): boolean {
  return ISIN_RE.test(v.trim().toUpperCase());
}

/**
 * THE stored form of a matched instrument: its EODHD source_code, or null when
 * the match carries none. Never a bare listing ticker, never an ISIN, never
 * what was typed — a null here means the caller has nothing it may store.
 */
export function canonicalCode(m: SymbolMatch | null | undefined): string | null {
  return m && typeof m.code === 'string' && m.code ? m.code : null;
}

/** The persistable warehouse link for a matched instrument: its code, which
 * resolves to exactly that instrument; the ISIN only when no code came back. */
export function linkFromMatch(m: SymbolMatch): WarehouseLink {
  const code = canonicalCode(m);
  return {
    ...(code ? { ticker: code } : m.isin ? { isin: m.isin } : {}),
    ...(m.name ? { name: m.name } : {}),
  };
}

/** Auto-detect the identifier kind and look it up: ISIN-shaped → isin, else ticker. */
export function lookupIdentifier(id: string): Promise<SymbolMatch[]> {
  const v = id.trim().toUpperCase();
  return isIsin(v) ? lookupSymbol({ isin: v }) : lookupSymbol({ ticker: v });
}

/** Tiered free-text search: ISIN → exact listing ticker → (fuzzy) name. */
export async function searchWarehouse(
  query: string,
  opts: { signal?: AbortSignal } = {},
): Promise<SymbolMatch[]> {
  const q = query.trim();
  if (q.length < 2) return [];
  if (isIsin(q)) return lookupSymbol({ isin: q.toUpperCase() }, opts);
  // Both legs take the signal: this is two sequential round-trips, so a
  // keystroke landing between them must cancel the second as well as the first.
  const byTicker = await lookupSymbol({ ticker: q.toUpperCase() }, opts);
  if (byTicker.length > 0 || q.length < 3) return byTicker;
  return lookupSymbol({ name: q }, opts);
}

/** A candidate the picker proposes: a match plus when its last bar was. */
export interface PickCandidate extends SymbolMatch {
  code: string;
  /** The last bar the serving layer holds (YYYY-MM-DD), null when none. */
  lastBar: string | null;
  /** lastBar lies within five trading days. */
  fresh: boolean;
}

/** What a typed string resolves to — see data-worker/src/symbols/_pick.ts. */
export interface PickAnswer {
  query: string;
  /** exact: the string IS a source_code. proposed: a person must choose.
   *  none: nothing found, and nothing is substituted. */
  status: 'exact' | 'proposed' | 'none';
  via: 'code' | 'isin' | 'ticker' | 'name' | null;
  candidates: PickCandidate[];
}

/**
 * The one ticker picker (WORK.md item 43): every entry point asks this and
 * stores only a candidate's `code`. `currency` is what the caller saw the
 * instrument priced in (it ranks candidates); `name` is searched when every
 * identifier stage misses. Throws on a network or server failure — a failed
 * pick is not a clean "none".
 */
export async function pickTicker(
  q: string,
  hints: { currency?: string; name?: string } = {},
  opts: { signal?: AbortSignal } = {},
): Promise<PickAnswer> {
  const qs = new URLSearchParams({ q: q.trim() });
  if (hints.currency && /^[A-Za-z]{3}$/.test(hints.currency)) qs.set('currency', hints.currency.toUpperCase());
  if (hints.name && hints.name.trim()) qs.set('name', hints.name.trim().slice(0, 120));
  const j = await getJsonOrThrow<PickAnswer>(
    `/api/symbols/pick?${qs.toString()}`, 'ticker pick',
    { credentials: 'include', signal: opts.signal },
  );
  return {
    query: typeof j.query === 'string' ? j.query : q,
    status: j.status === 'exact' || j.status === 'proposed' ? j.status : 'none',
    via: j.via ?? null,
    candidates: Array.isArray(j.candidates) ? j.candidates.filter((c) => c && typeof c.code === 'string' && c.code) : [],
  };
}

/** Does the warehouse hold ANY daily values for this instrument? Soft-false. */
export async function hasWarehouseDailyValues(link: WarehouseLink): Promise<boolean> {
  try {
    const series = await fetchWarehousePrices(link);
    return Object.keys(series).length > 0;
  } catch {
    return false;
  }
}

/** One observed price point — an executed trade price or a statement EOD price. */
export interface PriceObs { date: string; price: number }

/** A price-search hit: a SymbolMatch plus how many observations it fit. */
export interface PriceMatch extends SymbolMatch { hits: number; total: number }

/**
 * Identify an instrument by PRICE when ISIN/name/ticker all miss: POST the
 * observed (date, price) points and get back warehouse instruments whose
 * daily range (or NAV) contains them on those dates, ranked by hit count.
 * Soft-fails to [] (warehouse not configured → 501, network error, etc.) so
 * the matcher just skips the price fallback.
 */
export async function searchByPrice(obs: PriceObs[], types?: string[]): Promise<PriceMatch[]> {
  const clean = obs.filter((o) => /^\d{4}-\d{2}-\d{2}$/.test(o.date) && isPositiveFinite(o.price));
  if (clean.length < 2) return [];
  try {
    const { ok, json } = await getJsonResult<{ matches?: PriceMatch[] }>('/api/symbols/price-search', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ obs: clean, ...(types && types.length ? { types } : {}) }),
    });
    if (!ok) return [];
    return dedupeMatches(Array.isArray(json?.matches) ? json.matches : []) as PriceMatch[];
  } catch {
    return [];
  }
}

/** Daily close/NAV series for a warehouse-matched instrument. {} when absent. */
export async function fetchWarehousePrices(link: WarehouseLink): Promise<Record<string, number>> {
  const qs = idQuery(['isin', link.isin], ['ticker', link.ticker]);
  if (!qs) return {};
  const j = await getJsonOrThrow<{ series?: Record<string, number> }>(`/api/symbols/prices?${qs}`, 'warehouse prices', { credentials: 'include' });
  return j.series && typeof j.series === 'object' ? j.series : {};
}

/** One corporate split event for a ticker: the ex-date and ratio (new/old shares). */
export interface SplitEvent { ex_date: string; ratio: number; }

/**
 * Daily split history for a ticker from the warehouse-backed /api/symbols/splits
 * endpoint. Soft-fails to [] on any HTTP/network/parse error (a missing split
 * feed is not an error) so the split-detection UI just shows nothing to apply.
 */
export async function fetchSplits(ticker: string, isin?: string): Promise<SplitEvent[]> {
  if (!ticker && !isin) return [];
  const query = idQuery(['ticker', ticker], ['isin', isin]);
  const j = await getJson<{ splits?: SplitEvent[] }>(
    `/api/symbols/splits?${query}`,
    { credentials: 'include' },
  );
  if (!j || !Array.isArray(j.splits)) return [];
  return j.splits.filter(
    (e) => e && typeof e.ex_date === 'string' && isPositiveFinite(e.ratio),
  );
}

/** One cash dividend for a ticker: ex-date + per-share amount in the
 *  instrument's PRICE unit (the endpoint scales GBP-declared amounts onto
 *  GBX/pence listings, so dividend ÷ close is dimensionless). As declared
 *  only when its feed's `basis` is 'raw-v1' or 'derived-v1' (see DividendFeed). */
export type DividendEvent = DividendPoint;

/** The /api/symbols/dividends answer: the rows, and since the 2026-10-12
 *  contract the history's basis and its count of rows left out. */
interface DividendsBody {
  dividends?: Array<{ ex_date?: string; amount?: number }>;
  basis?: unknown;
  unresolved?: unknown;
}

/**
 * Per-company cash-dividend history from the warehouse-backed
 * /api/symbols/dividends endpoint (each instrument's own histz_ blob, so ANY
 * ticker with data in the warehouse is covered — not just a tracked shortlist).
 *
 * The answer carries its BASIS (the dividend basis contract with
 * capember/market-data, 2026-10-12): `basis: 'raw-v1'` means every row is the
 * amount as declared, per share, with the rows whose declared amount could
 * not be established left out and counted in `unresolved`; `basis:
 * 'derived-v1'` (Japan's, market-data #16, 2026-10-13) is the same promise on
 * the amounts with each ex-date derived from the record date by the TSE
 * settlement cycle, kept apart from raw-v1 so the page can say so; an absent
 * or unknown basis is `'legacy'`, the mixed history in which the backfill's rows
 * are split-adjusted to its own day and the nightly's are as declared (audit
 * MONEY-7), so no row of it may be read as declared.
 *
 * A failed read is not a clean one: an HTTP error (the Worker answers a blob
 * it cannot read with 502), a network failure, an unparseable body or a body
 * with no `dividends` list is `{ ok: false }`, never an empty history. An
 * instrument the warehouse does not know answers 200 with no rows, which IS
 * an empty history.
 */
export async function fetchDividends(ticker: string, isin?: string): Promise<DividendFeed> {
  if (!ticker && !isin) return { ok: true, basis: 'legacy', unresolved: 0, dividends: [] };
  const query = idQuery(['ticker', ticker], ['isin', isin]);
  let res: { ok: boolean; status: number; json: DividendsBody | null };
  try {
    res = await getJsonResult<DividendsBody>(`/api/symbols/dividends?${query}`, { credentials: 'include' });
  } catch (e) {
    return { ok: false, error: `dividend history unreachable (${e instanceof Error ? e.message : String(e)})` };
  }
  if (!res.ok) return { ok: false, error: `dividend history read failed (HTTP ${res.status})` };
  const j = res.json;
  if (!j || !Array.isArray(j.dividends)) {
    return { ok: false, error: 'dividend history unreadable (no dividends list)' };
  }
  const out: DividendEvent[] = [];
  for (const d of j.dividends) {
    if (d && typeof d.ex_date === 'string' && typeof d.amount === 'number' && isPositiveFinite(d.amount)) {
      out.push({ exDate: d.ex_date, amount: d.amount });
    }
  }
  const basis: DividendBasis =
    j.basis === 'raw-v1' || j.basis === 'derived-v1' ? j.basis : 'legacy';
  const unresolved =
    typeof j.unresolved === 'number' && Number.isInteger(j.unresolved) && j.unresolved > 0
      ? j.unresolved
      : 0;
  return { ok: true, basis, unresolved, dividends: out };
}
